import moment from 'moment';

import { Group, GroupMissingField, Invited, ProgramItem, SLOT_DURATION_MINUTES } from './types';

/** Formate un créneau "YYYY-MM-DD HH:mm" en "… HH:mm → HH:mm" (fin = début + durée) */
export function formatSlotRange(slot?: string): string {
  if (!slot) return '';
  const start = moment(slot, 'YYYY-MM-DD HH:mm');
  if (!start.isValid()) return slot;
  const end = start.clone().add(SLOT_DURATION_MINUTES, 'minutes');
  return `${start.format('dddd D MMMM YYYY - HH:mm')} → ${end.format('HH:mm')}`;
}

/** Libellé lisible d'un élément de programme (chaîne brute ou objet) */
export function getProgramLabel(item: ProgramItem): string {
  if (typeof item === 'string') return item;
  return item.label || item.name || item.title || item.value || item._id || '';
}

/** Champs du groupe qui ne contiennent jamais d'invités (programme, invités déjà en base…) */
const NON_GUEST_FIELDS = ['children', 'conferences', 'demos', 'invitedUsers', 'program'];

const FIRST_NAME_KEYS = ['firstName', 'firstname', 'first_name', 'prenom', 'prénom'];
const LAST_NAME_KEYS = ['lastName', 'lastname', 'last_name', 'nom'];
const EMAIL_KEYS = ['email', 'mail', 'emailAddress'];

function pick(raw: Record<string, unknown>, keys: string[]): string | undefined {
  const key = keys.find((k) => typeof raw[k] === 'string' && (raw[k] as string).trim());
  return key ? (raw[key] as string).trim() : undefined;
}

/**
 * Normalise un invité saisi dans le formulaire du groupe.
 * Accepte un email brut ("jean@x.fr") ou un objet ({ firstName, lastName, email }).
 * Renvoie `undefined` si l'élément ne ressemble pas à un invité.
 */
function normalizeGuest(raw: unknown): Omit<Invited, '_id' | 'collection'> | undefined {
  if (typeof raw === 'string') {
    const value = raw.trim();
    if (!value) return undefined;
    return value.includes('@') ? { email: value } : { lastName: value };
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const item = raw as Record<string, unknown>;
  const email = pick(item, EMAIL_KEYS);
  const firstName = pick(item, FIRST_NAME_KEYS);
  const lastName = pick(item, LAST_NAME_KEYS);
  // Un invité a forcément un email ou un nom : ça évite de confondre avec
  // un élément de programme ({ _id, label }) ou n'importe quel autre tableau.
  if (!email && !firstName && !lastName) return undefined;
  const status = typeof item.status === 'string' ? (item.status as string) : undefined;
  return {
    email,
    firstName,
    lastName,
    status,
    pmr: item.pmr === 'yes' ? 'yes' : undefined,
    parking: item.parking === 'yes' ? 'yes' : undefined,
    licensePlate: typeof item.licensePlate === 'string' ? item.licensePlate : undefined,
  };
}

/** Clé de dédoublonnage : l'email s'il existe, sinon prénom + nom */
export function getGuestKey(guest: Pick<Invited, 'email' | 'firstName' | 'lastName'>): string {
  if (guest.email) return guest.email.trim().toLowerCase();
  return `${guest.firstName || ''} ${guest.lastName || ''}`.trim().toLowerCase();
}

/**
 * Invités saisis dans le formulaire du groupe (champ répétable), avant que
 * les fiches `invited` correspondantes n'existent en base.
 *
 * Si `guestsField` n'est pas configuré, on cherche dans tous les champs du
 * groupe le premier tableau dont les éléments ressemblent à des invités.
 */
export function getGroupGuests(group: Group, guestsField?: string): Invited[] {
  const source = (group as unknown) as Record<string, unknown>;
  const fields = guestsField
    ? [guestsField]
    : Object.keys(source).filter((key) => !NON_GUEST_FIELDS.includes(key));

  const field = fields.find((key) => {
    const value = source[key];
    return Array.isArray(value) && value.some((item) => normalizeGuest(item));
  });
  if (!field) return [];

  return (source[field] as unknown[])
    .map((item, index) => {
      const guest = normalizeGuest(item);
      if (!guest) return undefined;
      return {
        ...guest,
        // Pas de fiche en base : id local, stable tant que la saisie ne bouge pas
        _id: `${group._id}--${field}-${index}`,
        collection: '',
        parentId: group._id,
        isDraft: true,
      } as Invited;
    })
    .filter((guest): guest is Invited => !!guest);
}

/**
 * Liste des invités du groupe : ceux enregistrés en base, complétés par ceux
 * saisis dans le formulaire dont la fiche n'existe pas encore.
 */
export function mergeGuests(registered: Invited[], groupGuests: Invited[]): Invited[] {
  const keys = new Set(registered.map(getGuestKey).filter(Boolean));
  const missing = groupGuests.filter((guest) => {
    const key = getGuestKey(guest);
    return !key || !keys.has(key);
  });
  return [...registered, ...missing];
}

/** Éléments manquants sur un groupe, dans l'ordre d'affichage */
export function getMissingGroupFields(group: Group & { invited?: Invited[] }): GroupMissingField[] {
  const missing: GroupMissingField[] = [];
  if (!group.slot) missing.push('slot');
  const program = [...(group.demos || []), ...(group.conferences || [])].filter((item) =>
    getProgramLabel(item),
  );
  if (!program.length) missing.push('program');
  if (!group.invited?.length) missing.push('guests');
  return missing;
}
